import { ReactNode, useState } from "react";

import { UpOutlined } from "@ant-design/icons";
import { Button, Switch } from "antd";
import styled from "styled-components";

export interface PanelIssue {
  /** Identifies the row across renders. */
  key: string;
  /** "error" paints the row and the badge red, and turns the whole panel red. */
  variant?: "error";
  title: string;
  subtitle?: string;
  /** Buttons or switches for the right of the row. */
  action?: ReactNode;
  testId?: string;
}

interface IssuesPanelProps {
  issues: PanelIssue[];
  /** Replaces the count of issues in the header bar. */
  header?: string;
  testId?: string;
}

/**
 * The box that collects what the user should look at before going on: one row per
 * issue, under a header that counts them and folds the list away. The errors come
 * first, and any one of them turns the header red.
 */
export default function IssuesPanel({ issues, header, testId }: IssuesPanelProps) {
  const [collapsed, setCollapsed] = useState(false);

  if (issues.length === 0) return null;

  // The count is what a caller with nothing better to say falls back to
  const headerText =
    header ||
    `${issues.length} issue${issues.length === 1 ? "" : "s"} ${
      issues.length === 1 ? "needs" : "need"
    } your review`;

  // The header bar reads red as soon as one row blocks, and amber when the panel
  // only carries warnings
  const headerVariant = issues.some((issue) => issue.variant === "error")
    ? "error"
    : undefined;

  // Both halves keep the order they were given, so only the two groups move
  const ordered = [
    ...issues.filter((issue) => issue.variant === "error"),
    ...issues.filter((issue) => issue.variant !== "error")
  ];

  return (
    <Panel data-testid={testId} $variant={headerVariant}>
      <PanelHeader
        type="button"
        $variant={headerVariant}
        aria-expanded={!collapsed}
        onClick={() => setCollapsed((prev) => !prev)}>
        <Badge $size={18} $variant={headerVariant} />
        <HeaderText>{headerText}</HeaderText>
        <Chevron $collapsed={collapsed} />
      </PanelHeader>

      {!collapsed && (
        <PanelBody>
          {ordered.map((issue) => (
            <IssueRow key={issue.key} data-testid={issue.testId} $variant={issue.variant}>
              <Badge $size={16} $variant={issue.variant} />
              <IssueText>
                <IssueTitle>{issue.title}</IssueTitle>
                {issue.subtitle && <IssueSubtitle>{issue.subtitle}</IssueSubtitle>}
              </IssueText>
              {issue.action && <IssueAction>{issue.action}</IssueAction>}
            </IssueRow>
          ))}
        </PanelBody>
      )}
    </Panel>
  );
}

const Panel = styled.div<{ $variant?: "error" }>`
  margin: 8px 0 10px 0;
  border: 1px solid ${({ $variant }) => ($variant === "error" ? "#ffccc7" : "#ffe1a8")};
  border-radius: 8px;
  background: #ffffff;
  overflow: hidden;
`;

const PanelHeader = styled.button<{ $variant?: "error" }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 16px;
  background: ${({ $variant }) => ($variant === "error" ? "#fff1f0" : "#fdf5e1")};
  border: none;
  border-bottom: 1px solid
    ${({ $variant }) => ($variant === "error" ? "#ffccc7" : "#f2ead4")};
  cursor: pointer;
  text-align: left;
`;

const HeaderText = styled.span`
  flex: 1;
  font-size: 14px;
  font-weight: 600;
  color: #1f1f1f;
`;

const Chevron = styled(UpOutlined)<{ $collapsed?: boolean }>`
  color: #8c8c8c;
  font-size: 12px;
  transition: transform 0.15s ease;
  transform: rotate(${({ $collapsed }) => ($collapsed ? "180deg" : "0deg")});
`;

const PanelBody = styled.div`
  display: flex;
  flex-direction: column;
`;

const IssueRow = styled.div<{ $variant?: "error" }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 16px;
  background: ${({ $variant }) => ($variant === "error" ? "#fff1f0" : "transparent")};

  & + & {
    border-top: 1px solid #f2ead4;
  }
`;

const IssueText = styled.div`
  flex: 1;
  min-width: 0;
`;

const IssueTitle = styled.div`
  font-size: 13px;
  color: #1f1f1f;
  overflow-wrap: anywhere;
`;

const IssueSubtitle = styled.div`
  margin-top: 1px;
  font-size: 12px;
  color: #8c8c8c;
  overflow-wrap: anywhere;
`;

const IssueAction = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  flex-shrink: 0;
  /* a row whose only action is an icon keeps the column an icon button is wide,
     so it lines up with the same control in a list below the panel */
  min-width: 22px;
`;

// Amber circular "!" badge, matching the icon style in the mockup. The exclamation is drawn
// via ::before so the same element serves the header and each row at different sizes. The
// "error" variant turns it red for blocking issues.
const Badge = styled.span<{ $size?: number; $variant?: "error" }>`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${({ $size }) => $size ?? 20}px;
  height: ${({ $size }) => $size ?? 20}px;
  border-radius: 50%;
  background: ${({ $variant }) => ($variant === "error" ? "#ff4d4f" : "#faad14")};
  color: #ffffff;
  font-weight: 700;
  font-size: ${({ $size }) => Math.round(($size ?? 20) * 0.62)}px;
  line-height: 1;

  &::before {
    content: "!";
  }
`;

// The mdi icon renders a bare <svg>, so antd's built-in ".anticon + span" spacing doesn't
// apply — lay the button out as an inline flex row with an explicit gap instead.
export const FilterButton = styled(Button)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

export const ActionLabel = styled.span`
  font-size: 13px;
  color: #595959;
  white-space: nowrap;
`;

export const BrandSwitch = styled(Switch)`
  &.ant-switch-checked {
    background: #00c7b1;
  }
`;
